import { useTranslation } from "react-i18next"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { AllProgressNote } from "@/modules/patients/detail/episode-of-care/components/all-progress-note"
import { EpisodeOfCareItemWrapper } from "@/modules/patients/detail/episode-of-care/components/episode-of-care-item"
import { EpisodeOfCareNode } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { chPatientEpisodesOfCareQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientEpisodesOfCareQuery.graphql"
import { dePatientEpisodesOfCareQuery$data } from "@/modules/patients/services/de/__generated__/dePatientEpisodesOfCareQuery.graphql"

type EpisodeOfCareGroupItem = Record<string, Array<{ data: EpisodeOfCareNode }>>

const EpisodeOfCareGroup = ({
  episodesOfCareGroup,
  groupKey,
  patientId,
  isEditMode,
}: {
  episodesOfCareGroup: Array<{ data: EpisodeOfCareNode }>
  groupKey: string
  patientId: string
  isEditMode: boolean
}) => {
  return (
    <div className="flex flex-col gap-0.5" data-testid={`episode-of-care-group-${groupKey}`}>
      <span className="text-on-surface-subdued px-2 py-1 text-xs">{groupKey}</span>
      {episodesOfCareGroup
        .sort((a, b) => b.data.updatedAt.localeCompare(a.data.updatedAt))
        .map(({ data: episodeOfCare }) => (
          <EpisodeOfCareItemWrapper
            key={episodeOfCare.id}
            episodeOfCare={episodeOfCare}
            patientId={patientId}
            isEditMode={isEditMode}
          />
        ))}
    </div>
  )
}

interface EpisodeOfCareNavigationProps {
  patientEpisodesOfCareRef: dePatientEpisodesOfCareQuery$data & chPatientEpisodesOfCareQuery$data
  appointmentId: string | null
  patientId: string
  isEditMode: boolean
  isDropdown: boolean
}

const EpisodeOfCareNavigation = ({
  patientEpisodesOfCareRef,
  appointmentId,
  patientId,
  isEditMode,
  isDropdown,
}: EpisodeOfCareNavigationProps) => {
  const { t } = useTranslation()

  const dataPatientEpisodesOfCareRef = patientEpisodesOfCareRef.patientEpisodesOfCare?.edges

  const dataPatientEpisodesOfCareData = dataPatientEpisodesOfCareRef?.map((e) => e.node)
  const episodesOfCareGroup = dataPatientEpisodesOfCareRef?.reduce<EpisodeOfCareGroupItem>((result, currentEpisode) => {
    const episodeOfCare = currentEpisode.node

    if (!episodeOfCare.isVirtual) {
      const yearFormat = new Date(episodeOfCare.updatedAt).getFullYear().toString()

      result[yearFormat] = result[yearFormat] || []
      result[yearFormat].push({ data: episodeOfCare })
    }

    return result
  }, {})

  const sortedKeys = (episodesOfCareGroup && Object.keys(episodesOfCareGroup).sort((a, b) => b.localeCompare(a))) || []

  return (
    <ScrollArea viewportClassName="max-h-none" className={cn("bg-surface h-full", { "border-r": !isDropdown })}>
      <div
        className={cn("grid grid-cols-1 grid-rows-[auto_auto_minmax(0,1fr)] content-start justify-end", {
          "animate-slide-in-left bg-surface h-full px-2 py-8 pb-12": !isDropdown,
        })}
        data-testid="episode-of-care-nav-wrapper">
        <AllProgressNote
          appointmentId={appointmentId}
          patientId={patientId}
          dataPatientEpisodesOfCareData={dataPatientEpisodesOfCareData}
          isEditMode={isEditMode}
        />
        <div className="border-t-border -mx-2 border-t"></div>
        <div data-testid="episode-of-care-left-nav-bar-section">
          <div className="h-10 px-2 py-2.5" data-testid="episode-of-care">
            <span className="text-on-surface-subdued truncate text-sm">{t("episodeOfCare.episodeOfCare")}</span>
          </div>
          {episodesOfCareGroup &&
            sortedKeys.map((year) => (
              <EpisodeOfCareGroup
                key={year}
                groupKey={year}
                episodesOfCareGroup={episodesOfCareGroup[year]}
                patientId={patientId}
                isEditMode={isEditMode}
              />
            ))}
        </div>
      </div>
    </ScrollArea>
  )
}

export { EpisodeOfCareNavigation }
